import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import Card from '../Card';
import { springConfig } from '../../utils/animations';

interface InputSectionProps {
  birthdate: string;
  setBirthdate: (date: string) => void;
  sex: string;
  setSex: (sex: string) => void;
}

const InputSection: React.FC<InputSectionProps> = ({
  birthdate,
  setBirthdate,
  sex,
  setSex
}) => {
  const prefersReducedMotion = useReducedMotion();

  // Animation variants for the input fields
  const inputVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        ...springConfig.gentle,
        delay: i * 0.15
      }
    }),
    focus: {
      scale: 1.02,
      transition: springConfig.snappy
    }
  };

  // Prompt animation for empty state
  const promptVariants = {
    animate: {
      opacity: [0.5, 1, 0.5],
      y: [0, -3, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut' as const
      }
    }
  };

  // Icon animation
  const iconVariants = {
    initial: { rotate: 0 },
    hover: {
      rotate: [0, -10, 10, 0],
      transition: { duration: 0.5 }
    }
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    return (
      <Card className="mb-8 p-6" animated={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
          <div>
            <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700 dark:text-catppuccin-text mb-2 flex items-center gap-2">
              <Calendar size={16} />
              Your Birthdate
            </label>
            <input
              id="birthdate"
              type="date"
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 transition-all dark:bg-catppuccin-surface0 dark:text-catppuccin-text dark:border-catppuccin-surface2"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
            />
          </div>
          <div>
            <label htmlFor="sex" className="block text-sm font-medium text-gray-700 dark:text-catppuccin-text mb-2">
              Sex (for life expectancy estimate)
            </label>
            <select
              id="sex"
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 transition-all dark:bg-catppuccin-surface0 dark:text-catppuccin-text dark:border-catppuccin-surface2"
              value={sex}
              onChange={(e) => setSex(e.target.value)}
            >
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </div>
        </div>
        {!birthdate &&
          <div className="text-center text-gray-500 dark:text-catppuccin-subtext1 mt-6">
            Please enter your birthdate to begin your journey.
          </div>
        }
      </Card>
    );
  }

  return (
    <Card className="mb-8 p-6" animated={true}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
        <motion.div
          variants={inputVariants}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <motion.label
            htmlFor="birthdate"
            className="block text-sm font-medium text-gray-700 dark:text-catppuccin-text mb-2 flex items-center gap-2"
            initial="initial"
            whileHover="hover"
          >
            <motion.span variants={iconVariants}>
              <Calendar size={16} />
            </motion.span>
            Your Birthdate
          </motion.label>
          <motion.input
            id="birthdate"
            type="date"
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 transition-colors dark:bg-catppuccin-surface0 dark:text-catppuccin-text dark:border-catppuccin-surface2"
            value={birthdate}
            onChange={(e) => setBirthdate(e.target.value)}
            max={new Date().toISOString().split("T")[0]}
            whileFocus={{ scale: 1.02, boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.3)' }}
            transition={springConfig.snappy}
          />
        </motion.div>
        <motion.div
          variants={inputVariants}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <label htmlFor="sex" className="block text-sm font-medium text-gray-700 dark:text-catppuccin-text mb-2">
            Sex (for life expectancy estimate)
          </label>
          <motion.select
            id="sex"
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 transition-colors dark:bg-catppuccin-surface0 dark:text-catppuccin-text dark:border-catppuccin-surface2"
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            whileFocus={{ scale: 1.02, boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.3)' }}
            transition={springConfig.snappy}
          >
            <option value="female">Female</option>
            <option value="male">Male</option>
          </motion.select>
        </motion.div>
      </div>
      {!birthdate &&
        <motion.div
          className="text-center text-gray-500 dark:text-catppuccin-subtext1 mt-6"
          variants={promptVariants}
          animate="animate"
        >
          Please enter your birthdate to begin your journey.
        </motion.div>
      }
    </Card>
  );
};

export default InputSection;
